import React from 'react';
import { Link, useLocation, useNavigation } from 'react-router-dom';
import { LayoutDashboard, Medal, Newspaper, Shield, Activity, Loader2 } from 'lucide-react';

interface MobileNavProps {
  isAdmin: boolean;
  unreadNewsCount: number;
}

import { prefetchRoute } from '../../router';

const IconWrapper = ({ children, isActive, isPending }: { children: React.ReactNode, isActive: boolean, isPending: boolean }) => (
  <div className={`transition-all duration-300 relative ${isActive ? 'scale-110' : 'scale-100'} ${isPending ? 'animate-pulse' : ''}`}>
    {children}
  </div>
);

// Loading indicator for the pending nav item
const NavIcon = ({ path, Icon, label, locationPathname, isNavigatingTo, onPrefetch }: {
  path: string,
  Icon: React.ElementType,
  label: string,
  locationPathname: string,
  isNavigatingTo: (path: string) => boolean,
  onPrefetch: (path: string) => void
}) => {
  const isActive = locationPathname === path;
  const isPending = isNavigatingTo(path);

  const activeClass = isActive
    ? 'text-indigo-600'
    : isPending
      ? 'text-indigo-400'
      : 'text-slate-500 hover:text-slate-700';

  return (
    <Link
      to={path}
      className={`flex flex-col items-center justify-center gap-0.5 w-full h-full rounded-2xl transition-all duration-300 relative ${activeClass}`}
      onTouchStart={() => onPrefetch(path)}
      onClick={() => onPrefetch(path)}
    >
      <IconWrapper isActive={isActive} isPending={isPending}>
        {isPending ? (
          <Loader2 size={22} className="animate-spin" />
        ) : (
          <Icon size={22} strokeWidth={isActive ? 2.5 : 2} className={isActive ? "drop-shadow-sm" : ""} />
        )}
      </IconWrapper>
      <span className={`text-[9px] font-bold tracking-tight mt-1 transition-opacity duration-300 ${isActive ? 'opacity-100' : 'opacity-70'}`}>{label}</span>
    </Link>
  );
};

const MobileNav: React.FC<MobileNavProps> = ({ isAdmin, unreadNewsCount }) => {
  const location = useLocation();
  const navigation = useNavigation();

  // Detect if we're navigating to a specific path
  const isNavigatingTo = (path: string) => {
    return navigation.state === 'loading' && navigation.location?.pathname === path;
  };

  const getNavClass = (path: string) => {
    const isActive = location.pathname === path;
    const isPending = isNavigatingTo(path);
    // Removed translate-y and background box for cleaner look
    return `flex flex-col items-center justify-center gap-0.5 w-full h-full rounded-2xl transition-all duration-300 relative ${isActive
      ? 'text-indigo-600'
      : isPending
        ? 'text-indigo-400'
        : 'text-slate-500 hover:text-slate-700'
      }`;
  };


  return (
    // Updated container: wider (mx-2), safe area bottom padding (mb-safe), standard height (h-16)
    <nav className="fixed bottom-2 left-2 right-2 z-50 md:hidden mb-safe">
      <div className="glass-panel rounded-2xl shadow-xl backdrop-blur-xl border border-white/50 bg-white/95 overflow-hidden">
        <div className="flex justify-around items-center h-16 w-full">
          <NavIcon path="/" Icon={LayoutDashboard} label="Cá nhân" locationPathname={location.pathname} isNavigatingTo={isNavigatingTo} onPrefetch={prefetchRoute} />
          <NavIcon path="/leaderboard" Icon={Medal} label="BXH" locationPathname={location.pathname} isNavigatingTo={isNavigatingTo} onPrefetch={prefetchRoute} />
          <NavIcon path="/feed" Icon={Activity} label="Hoạt động" locationPathname={location.pathname} isNavigatingTo={isNavigatingTo} onPrefetch={prefetchRoute} />

          <Link
            to="/news"
            className={getNavClass('/news')}
            onTouchStart={() => prefetchRoute('/news')}
            onClick={() => prefetchRoute('/news')}
          >
            <div className="relative flex flex-col items-center">
              <IconWrapper isActive={location.pathname === '/news'} isPending={isNavigatingTo('/news')}>
                {isNavigatingTo('/news') ? (
                  <Loader2 size={22} className="animate-spin" />
                ) : (
                  <Newspaper size={22} strokeWidth={location.pathname === '/news' ? 2.5 : 2} className={location.pathname === '/news' ? "drop-shadow-sm" : ""} />
                )}
              </IconWrapper>
              {unreadNewsCount > 0 && (
                <span className="absolute -top-1 right-2 w-2.5 h-2.5 bg-red-500 rounded-full border border-white animate-pulse"></span>
              )}
              <span className={`text-[9px] font-bold tracking-tight mt-1 transition-opacity duration-300 ${location.pathname === '/news' ? 'opacity-100' : 'opacity-70'}`}>Tin tức</span>
            </div>
          </Link>

          {isAdmin && (
            <NavIcon path="/admin" Icon={Shield} label="Admin" locationPathname={location.pathname} isNavigatingTo={isNavigatingTo} onPrefetch={prefetchRoute} />
          )}
        </div>
      </div>
    </nav>
  );
};

export default MobileNav;
